import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { faArrowLeft, faBox, faCalendarCheck, faChartLine, faEllipsisVertical, faLayerGroup, faListUl, faLocationDot, faPlus, faRightLeft, faTags, faThLarge, faTrashAlt, faTruck, faTruckMoving } from '@fortawesome/free-solid-svg-icons';
import { faPen, faPrint, faSlidersH, faTrashRestore } from '@fortawesome/pro-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { ColumnModel } from '@syncfusion/ej2-angular-grids';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { filter, pairwise } from 'rxjs/operators';
import { AuthService } from '../../../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { SessionService } from '../../../../../../@core/utils/session.service';
import { ConfirmationDialogComponent } from '../../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { PrinterDTO } from '../../../../estimates/models/printer-dto';
import { BinTagService } from '../../../../estimates/services/bin-tag.service';
import { InventoryService } from '../../../../pick-plan/services/inventory.service';
import { HandlingUnitDetailDTO } from '../../../models/handling-unit-detail';
import { HandlingUnitMasterDTO, handlingUnitLocation } from '../../../models/handling-unit-master';
import { InventoryStatusDTO, inventoryStatusClass } from '../../../models/inventory-status';
import { HandlingUnitDetailEditComponent } from '../../handling-unit-detail-edit/handling-unit-detail-edit.component';
import { InventoryAddItemPopupComponent } from '../../inventory-items/inventory-add-item-popup/inventory-add-item-popup.component';
import { InventoryTransferService } from '../../inventory-transfer/inventory-transfer.service';
import { InventoryAdjustQtyPopupComponent } from '../inventory-adjust-qty-popup/inventory-adjust-qty-popup.component';
import { InventoryMoveLocationPopupComponent } from '../inventory-move-location-popup/inventory-move-location-popup.component';
import { RemoveInventoryConfirmationComponent } from './remove-inventory-confirmation/remove-inventory-confirmation.component';
import { RestoreItemPopupComponent } from './restore-item-popup/restore-item-popup.component';
import { KW_QUANTITY_FORMAT } from '../../../../../../@core/utils/ejs-constants';
import { packageBreakdown } from '../../../models/package-breakdown';

// Grid/card row: the detail plus its flattened attributes (attr0..n) and a parsed expiration.
type HumItemRow = HandlingUnitDetailDTO & Record<`attr${number}`, string> & {
  expiration: Date | null;
  attributeSummary: string;
  /** "14 Box + 2 Bag" for packages with components, else empty. */
  packageSummary: string;
};

@Component({
  selector: 'inventory-summary-popup',
  templateUrl: './inventory-summary-popup.component.html',
  styleUrls: ['./inventory-summary-popup.component.scss']
})
export class InventorySummaryPopupComponent implements OnInit {

  @ViewChild('lotTemplate', { static: true }) public lotTemplate: any;
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;
  @ViewChild('quantityTemplate', { static: true }) public quantityTemplate: any;

  private destroyRef = inject(DestroyRef);
  public readonly getStatusClass = inventoryStatusClass;
  public gridColumns: ColumnModel[] = [];
  public itemRows: HumItemRow[] = [];

  // Icons //
  public faArrowLeft = faArrowLeft;
  public faBox = faBox;
  public faEllipsisVertical = faEllipsisVertical;
  public faLayerGroup = faLayerGroup;
  public faListUl = faListUl;
  public faLocationDot = faLocationDot;
  public faPen = faPen;
  public faRightLeft = faRightLeft;
  public faSlidersH = faSlidersH;
  public faChartLine = faChartLine;
  public faThLarge = faThLarge;
  public faTags = faTags;
  public faTruckMoving = faTruckMoving;
  public faCalendarCheck = faCalendarCheck;
  public faTruck = faTruck;
  faPrint = faPrint;
  faTrashRestore = faTrashRestore;

  public faTrashAlt = faTrashAlt;
  public isLoading: boolean = false;
  public humId: number;
  private previousUrl: string;
  public hum: HandlingUnitMasterDTO;
  public removedDetails: HandlingUnitDetailDTO[] = [];
  public humIsActive: boolean = true;
  public printers: PrinterDTO[] = [];
  public selectedPrinter: PrinterDTO = {};
  faPlus = faPlus;

  public newStatusId = 0;
  public newOrderNumber = "";
  

  public allStatuses: InventoryStatusDTO[] = [];
  public isPrint: boolean = false;
  public BottomSheetSubsciption: Subscription;

  constructor(
    //private pickPlanSessionService: PickPlanSessionService,
    //private pickPlanService: PickPlanService,
    private errorService: ErrorService,
    private router: Router,
    private route: ActivatedRoute,
    private changeDetRef: ChangeDetectorRef,
    private toastr: NbToastrService,
    private inventoryService: InventoryService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<InventorySummaryPopupComponent>,
    public authService: AuthService,
    public sessionService: SessionService,
    private binTagService: BinTagService,
    private inventoryTransferService: InventoryTransferService
    //@Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params.humId) {
        this.humId = params.humId;
        // Callers do not always supply isActive - the loaded HUM is what decides it.
        this.humIsActive = params.isActive !== 'false';
      } else {
        this.router.navigateByUrl("/harvest-engine/inventory/inventory-summary");
      }
    });
  }


  ngOnInit(): void {
    this.getPrinters();
    this.isLoading = true;
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        pairwise(), // check it's second route load
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e: any[]) => {
        this.previousUrl = e[0].url;
      });

    this.inventoryService.getInventoryStatuses().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
      this.allStatuses = x;
    }, error:err => { this.errorService.handleError(err); }});

    if (this.humId) {
      this.loadHum();
    }
  }

  private loadHum(): void {
    this.isLoading = true;

    // Inactive details come back only so the Restore action knows they exist - they are kept off the HUM itself.
    this.inventoryService.getInventoryHUM(this.humId.toString(), true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
      this.hum = x;
      this.humIsActive = x.isActive;
      this.newStatusId = x.inventoryStatusId;
      this.newOrderNumber = x.orderNumber;

      let details = x.handlingUnitDetails || [];
      this.removedDetails = this.humIsActive ? details.filter(hud => !hud.isActive) : [];
      this.hum.handlingUnitDetails = this.humIsActive ? details.filter(hud => hud.isActive) : details;
      this.buildItemGrid();

      this.isLoading = false;
      this.changeDetRef.detectChanges();
    }, error:err => {
      this.errorService.handleError(err);
      this.isLoading = false;
    }});
  }


  lotNumberClick(event) {
    if(this.authService.getCurrentUser().tenant.tenantId == 'b0ed668d-7ef2-4a23-a333-94ad278f45d7') {
      this.router.navigateByUrl('/harvest-engine/laboratory/lot-dashboard/' + event.lotId)
    } else {
      this.router.navigateByUrl('/harvest-engine/inventory/lot-dashboard/' + event.lotId)
    }
  }


  onNoClick(): void {
    this.dialogRef.close();
  }

  async removeInventory() {
    var val = await this.statusWarningPopup();

    if (val) {
      RemoveInventoryConfirmationComponent.open(this.dialog, { label: this.hum.barcode })
        .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {

        if (result?.result == true) {

          this.isLoading = true;

          if (result.transactionReasonCodeId) {
            this.hum.transactionReasonCodeId = result.transactionReasonCodeId;
          }

          this.isLoading = true;
          this.hum.isActive = false;

          this.inventoryService.putInventoryHUM(this.hum).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            this.back();
          });
        }
      });
    }
  }

  setStatus() {
    var newStatus = this.allStatuses.find(x => x.id == this.newStatusId);
    const confRef = this.dialog.open(ConfirmationDialogComponent, {
      minWidth: '10%',
      data: {
        confirmLabel: 'Yes',
        title: 'Update Status',
        confirmMessage: 'Are you sure you want to update the status for ' + this.hum.barcode + '?',
        cancelLabel: 'No'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (result == true) {
        this.hum.inventoryStatusId = this.newStatusId;
        this.hum.inventoryStatusIsLocked = newStatus.isLocked;
        this.hum.inventoryStatusIsWarning = newStatus.isWarning;
        this.hum.inventoryStatusName = newStatus.name;
        this.isLoading = true;
        this.inventoryService.putInventoryHUM(this.hum).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
          this.isLoading = false;
          this.toastr.success("Successfully updated inventory", "Success");
        });
      } else {
        this.newStatusId = this.hum.inventoryStatusId;
      }
    });
  }

  updateOrder() {
    const confRef = this.dialog.open(ConfirmationDialogComponent, {
      minWidth: '10%',
      data: {
        confirmLabel: 'Yes',
        title: 'Update Order #',
        confirmMessage: 'Are you sure you want to update the order for ' + this.hum.barcode + '?',
        cancelLabel: 'No'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (result == true) {
        this.hum.orderNumber = this.newOrderNumber;
        this.isLoading = true;
        this.inventoryService.putInventoryHUM(this.hum).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
          this.isLoading = false;
          this.toastr.success("Successfully updated order", "Success");
        });
      } else {
        this.newOrderNumber = this.hum.orderNumber;
      }
    });
  }

  async transferInventory() {
    var val = await this.statusWarningPopup();

    if (val) {
      // Activate HUM before transfer (affects entire HUM)
      this.activateHum();

      this.inventoryTransferService.sourceHum = this.hum;

      this.router.navigateByUrl('/harvest-engine/inventory/inventory-transfer');
    }
  }

  async adjustQty() {
    var val = await this.statusWarningPopup();

    if (val) {
      // Note: HUM and specific HUDs will be activated by the adjustment component based on which items are modified
      InventoryAdjustQtyPopupComponent.open(this.dialog, { Hum: JSON.parse(JSON.stringify(this.hum)) })
        .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(hum => {
          if (hum)
            this.loadHum();
        });
    }
  }

  private activateHum() {
    this.hum.isActive = true;
    this.humIsActive = true;
  }

  private activateHumAndSpecificHud(hudId?: number) {
    // Set HUM to active
    this.hum.isActive = true;
    this.humIsActive = true;

    // Set specific HUD to active if hudId is provided
    if (hudId && this.hum.handlingUnitDetails) {
      for (let i = 0; i < this.hum.handlingUnitDetails.length; i++) {
        if (this.hum.handlingUnitDetails[i].id === hudId) {
          this.hum.handlingUnitDetails[i].isActive = true;
          break;
        }
      }
    }
  }

  async statusWarningPopup() {
    var continueVar = true;

    if (this.hum.inventoryStatusIsLocked) {
      const confRef = this.dialog.open(ConfirmationDialogComponent, {
        minWidth: '10%',
        data: {
          confirmLabel: 'Ok',
          title: 'Inventory Status',
          confirmMessage: 'Inventory has a status of ' + this.hum.inventoryStatusName + ' and cannot be edited.',
          cancelLabel: 'No',
          isWarning: true
        }
      });

      confRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
        continueVar = false;
      });

      await confRef.afterClosed().toPromise();
    }
    else if (this.hum.inventoryStatusIsWarning) {
      const confRef = this.dialog.open(ConfirmationDialogComponent, {
        minWidth: '10%',
        data: {
          confirmLabel: 'Yes',
          title: 'Inventory Status',
          confirmMessage: 'Inventory has a status of ' + this.hum.inventoryStatusName + ', would you like to continue?',
          cancelLabel: 'No'
        }
      });

      confRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
        continueVar = result;
      });

      await confRef.afterClosed().toPromise();
    }

    return continueVar;
  }

  getRowQTY(row: HandlingUnitMasterDTO) {

    if (row && row.handlingUnitDetails)
      return row.handlingUnitDetails.reduce((acc, cur) => acc + cur.quantity, 0);
  }

  async moveInventory() {
    var val = await this.statusWarningPopup();

    if (val) {
      // Activate HUM before move (affects entire HUM location)
      this.activateHum();

      const hum = { ...JSON.parse(JSON.stringify(this.hum)), isMultiple: false };
      InventoryMoveLocationPopupComponent.open(this.dialog, { hum })
        .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(moved => {
          if (moved)
            this.loadHum();
        });
    }
  }

  async addItem() {
    var val = await this.statusWarningPopup();

    if (val) {
      // Activate HUM only - new HUD will be created as active by the add item component
      this.activateHum();

      InventoryAddItemPopupComponent.open(this.dialog, { Hum: JSON.parse(JSON.stringify(this.hum)) })
        .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(hum => {
          if (hum)
            this.loadHum();
        });
    }
  }

  duplicateInventory() {
    this.isLoading = true;
    let newHum = JSON.parse(JSON.stringify(this.hum));
    newHum.id = 0;
    newHum.handlingUnitDetails.forEach(element => {
      element.id = 0;
    });
    this.inventoryService.postInventoryHUM(newHum).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
      this.isLoading = false;
      this.dialogRef.close();
    }, error:err => {
      this.errorService.handleError(err);
      this.isLoading = false;
  }});
  }

  getLocation(): string {
    return handlingUnitLocation(this.hum);
  }


  convertDate(date: Date) {
    return moment(date).format('LL');
  }

  updateHUM(hum) {
    this.hum = hum;
    this.buildItemGrid();
    this.changeDetRef.detectChanges();
  }

  get statusName(): string | undefined {
    return this.allStatuses.find(x => x.id == this.newStatusId)?.name ?? this.hum?.inventoryStatusName;
  }

  trackByItem(_: number, row: HumItemRow): number | undefined {
    return row.id;
  }

  private packageSummary(hud: HandlingUnitDetailDTO): string {
    if (!hud.packageHasComponents || !hud.packageUnitOfMeasure || !hud.packageComponentName) return '';
    const { packages, components } = packageBreakdown(hud.quantity, hud.packageComponentQuantityPerPackage);
    return `${packages} ${hud.packageUnitOfMeasure} + ${components} ${hud.packageComponentName}`;
  }

  /** Attribute columns follow the first item's attributes, as the old e-column loop did. */
  private buildItemGrid(): void {
    const details = this.hum?.handlingUnitDetails ?? [];
    const attributes = details[0]?.itemAttributeValues ?? [];

    this.itemRows = details.map(d => {
      const values = attributes.map((_, i) => d.itemAttributeValues?.[i]?.value ?? '');
      // The backend sends DateTime.MinValue (year 0001) for "no expiration".
      const expiration = d.lotExperationDate ? moment(d.lotExperationDate) : null;
      return Object.assign({}, d, ...values.map((v, i) => ({ [`attr${i}`]: v })), {
        expiration: expiration?.isValid() && expiration.year() > 1 ? expiration.toDate() : null,
        attributeSummary: values.filter(v => v).join(' · '),
        packageSummary: this.packageSummary(d)
      }) as HumItemRow;
    });

    this.gridColumns = [
      { field: 'itemName', headerText: 'Product' },
      { field: 'lotNumber', headerText: 'Lot', template: this.lotTemplate },
      { field: 'quantity', headerText: 'Quantity', format: KW_QUANTITY_FORMAT, template: this.quantityTemplate },
      { field: 'varietyName', headerText: 'Variety' },
      ...attributes.map((a, i): ColumnModel => ({ field: `attr${i}`, headerText: a.name })),
      { field: 'expiration', headerText: 'Expiration', type: 'date', format: 'M/d/yy' },
      { headerText: '', width: 80, template: this.actionTemplate }
    ];
  }

  back() {
    if (this.previousUrl) {
      this.router.navigateByUrl(this.previousUrl);
    } else {
      this.router.navigateByUrl('/harvest-engine/inventory/inventory-summary');
    }
  }

  reprintInventory() {
    if (this.isPrint == false) {
      this.isPrint = true;
    } else {
      this.isPrint = false;
    }
  }

  getPrinters() {
    if (this.sessionService.printers) {
      this.printers = this.sessionService.printers;
    } else if (!this.sessionService.printers) {
      this.binTagService.getPrinters().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:p => {
        this.printers = p.filter(x => x.isActive);
      }, error:err => this.errorService.handleError(err)});
    }
  }

  selectPrinter(printerId: number) {
    this.selectedPrinter = this.printers.filter(x => x.id === printerId)[0];

  }

  print() {
    this.inventoryService.reprintInventoryHUM(this.hum.id.toString(), this.selectedPrinter.id.toString()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
      this.toastr.success('Print job sent successfully', 'SUCCCESS');
    }, error:err => {
      this.isLoading = false;
      this.errorService.handleError(err);
    }});
  }

  restoreRemovedItem() {
    RestoreItemPopupComponent.open(this.dialog, { details: this.removedDetails })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (!result || !result.detailId)
        return;

      let detail = this.removedDetails.find(x => x.id == result.detailId);
      this.isLoading = true;

      this.inventoryService.reactivateInventoryHUD(result.detailId, result.transactionReasonCodeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next: x => {
        this.toastr.success('Successfully restored ' + (detail ? detail.itemName : 'item'), 'Success');
        this.loadHum();
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }});
    });
  }

  edit(row: HandlingUnitDetailDTO) {
    // Grid/card rows carry display-only fields; edit the detail the API sent.
    const detail = this.hum.handlingUnitDetails.find(x => x.id === row.id) ?? row;

    // Activate HUM and specific HUD being edited
    this.activateHumAndSpecificHud(detail.id);

    HandlingUnitDetailEditComponent.open(this.dialog, { hud: JSON.parse(JSON.stringify(detail)), barcode: this.hum.barcode })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadHum());
  }

}
